import axios from 'axios';

import features from '@common/constants/features';

import { apiManager } from '@/api/api-manager/api-manager';
import { PointTypeEnum } from '@/components/product/info-blocks/types';
import { PAYMENT_METHODS } from '@/constants/payment';
import { cartGtmEvents } from '@/gtm/cart';
import { commonGtmEvents } from '@/gtm/common-gtm-events';
import { typGtmEvents } from '@/gtm/typ-gtm-events';
import { createOrdersSessionStorage } from '@/services/storage/orders-storage';
import { getCsmCommunicationCode } from '@/utils/cookies/csm-cookies';
import addressToString from '@/utils/location/addressToString';
import getErrorNotification from '@/utils/store/get-error-notification';

import { SOLDOUT_GROUP, UNALLOCATED_GROUP } from '../consts';

import { fromDeliveryVariantsToIntervals } from './utils/fromDeliveryVariantsToIntervals';
import { getDeliverySelectedDate } from './utils/getDeliverySelectedDate';

let cancelReqContact;

export default {
  namespaced: true,

  state: () => ({
    updateProgress: false,
    version: '',
    productCount: 0,
    contact: {},
    deliverySelectorInfo: {},
    adjustScrollNeeded: false,
    shippingGroups: [],
    channels: [],
    carts: [],
    deliveryInfo: {},
    paymentMethods: [],
    promoCodes: [],
    forbiddenPromotions: [],
    bonuses: {},
    subtotal: '',
    pickupStores: {},
    thirdpartyStores: {},
    subscribe: false,
    comment: '',
    cartId: '',
    isLoading: false,
    cartDetails: {},
    widgetDataType: '',
    toCartProgress: false,
    toCartSuccess: false,
    // вся информация о продуктах из корзины
    products: {},
    productsInfo: {},
  }),

  getters: {
    // Common info
    isLoading: (state) => state.isLoading,
    carts: (state) => state.carts || [],
    isCartConfirmationRequired: (state) => state.carts?.length > 1,
    updateProgress: (state) => state.updateProgress,
    widgetDataType: (state) => state.widgetDataType,
    version: (state) => state.version,
    contact: (state) => state.contact,
    bonuses: (state) => state.bonuses,
    cartDetails: (state) => state.cartDetails,
    productCount: (state) => state.productCount,
    // продукты из шиппинг группы
    products: (state) => state.shippingGroups.reduce((acc, g) => [...acc, ...g.products], []),
    productsIdExpressDelivery: (state) => (state.productsInfo
      ? Object.values(state.productsInfo)
        .filter((product) => product?.availability?.expressDelivery)?.map((product) => product.id) : []),
    unsoldProducts: (state, getters) => getters.unsoldShippingGroups.reduce((acc, g) => [...acc, ...g.products], []),
    adjustScrollNeeded: (state) => state.adjustScrollNeeded,
    toCartProgress: (state) => state.toCartProgress,
    toCartSuccess: (state) => state.toCartSuccess,

    // Shipping channels
    channels: (state) => state.channels,
    deliveryChannel: (state) => state.channels.find(({ type } = {}) => type === 'delivery'),
    pickupChannel: (state) => state.channels.find(({ type } = {}) => type === 'pickup'),

    // Payment methods, promos and prices
    paymentMethods: (state) => state.paymentMethods,
    isOnlinePaySelected: (state) => state.paymentMethods
      .find(({ method, selected }) => method === PAYMENT_METHODS.cardOnline && selected),
    isFpsPaySelected: (state) => state.paymentMethods
      .find(({ method, selected }) => method === PAYMENT_METHODS.fps && selected),
    isInstallmentPaySelected: (state) => state.paymentMethods
      .find(({ method, selected }) => method === PAYMENT_METHODS.installmentSovcombank && selected),
    subtotal: (state) => state.subtotal,
    forbiddenPromotions: (state) => state.forbiddenPromotions,
    promoCodes: (state) => state.promoCodes || [],
    appliedPromoCode: (state) => state.promoCodes?.[0]?.code || '',
    promoCodeInfo: (state, getters) => {
      const promoCodeInfo = getters.unsoldProducts.reduce((acc, { discounts = [], lineIds = [] } = {}) => {
        const promocode = discounts.find((d) => d.type === 'promocode');

        if (promocode) {
          acc.count += lineIds.length;
          acc.totalDiscount += parseFloat(promocode.value);
          if (promocode.bonusInteraction) acc.bonusInteraction.push(promocode.bonusInteraction);
        }

        return acc;
      }, { count: 0, totalDiscount: 0.00, bonusInteraction: [] });

      promoCodeInfo.bonusInteraction = promoCodeInfo.bonusInteraction.some((p) => p);

      return promoCodeInfo;
    },

    // Stores
    pickupStores: (state) => state.pickupStores || {},
    thirdpartyStores: (state) => state.thirdpartyStores || {},

    // Shipping groups and delivery info
    shippingGroups: (state) => state.shippingGroups,
    unsoldShippingGroups: (state) => state.shippingGroups.filter(({ id }) => id !== 'soldout'),
    existsUnallocatedGroupWithDelivery: (state) => state.shippingGroups.some(({ shippingChannels, type: typeGroup }) => typeGroup === 'unallocated' && shippingChannels.some(({ type, available }) => (type === 'delivery' && available))),
    existsUnallocatedGroupAlternativeDelivery: (state) => state.shippingGroups.some(({ shippingChannels, type: typeGroup }) => typeGroup === 'unallocated' && shippingChannels.some(({ type, available }) => (type !== 'delivery' && available))),
    isSoldoutGroup: (state) => state.shippingGroups.some(({ id }) => id === 'soldout'),
    deliveryShippingGroup: (state) => state.shippingGroups.find(({ type }) => type === 'delivery'),
    deliveryInfo: (state) => state.deliveryInfo,
    deliverySelectedDate: (state) => {
      if (!state.deliveryInfo?.interval) return;
      let selectedInterval;

      const date = state.deliveryInfo.interval.find((dateItem) => {
        const timeInterval = dateItem.time.find((time) => time.selected);
        if (timeInterval) {
          selectedInterval = timeInterval;
        }

        return !!timeInterval;
      });

      if (!date) return;

      return getDeliverySelectedDate(date, selectedInterval);
    },

    getDeliveryDateById: (state, getters) => (intervalId) => {
      if (!state.deliveryInfo?.interval) return;
      let selectedInterval;

      const timeId = intervalId || getters.deliverySelectedDate?.intervalId;

      const date = state.deliveryInfo.interval.find(({ time }) => {
        selectedInterval = time.find(({ id }) => id === timeId);
        return !!selectedInterval;
      });

      if (!date) return;

      return getDeliverySelectedDate(date, selectedInterval);
    },
    deliveryAddress: (state) => addressToString(state.deliveryInfo.address) || '',
    deliverySelectorInfo: (state) => state.deliverySelectorInfo,
  },

  actions: {
    async loadCart({ commit, dispatch, rootGetters }) {
      commit('setProgress');

      try {
        const cart = await this.$api.loadCart({
          isExpressDelivery: rootGetters['features/$isFeatureEnabled'](features.EXPRESS_DELIVERY),
        });

        await dispatch('setCart', cart);
        commit('setProgress', false);
      } catch (error) {
        commit('setCart');
        commit('setProgress', false);
      }
    },

    async loadHeaderCart({ commit, dispatch }) {
      commit('setProgress');

      try {
        const { products, quantity } = await this.$api.loadHeaderCart();
        // TODO: вынести в отдельную структуру чтоб не мешало работать нотификейшинам
        await dispatch('setCart', {
          productCount: quantity,
          shippingGroups: [{
            type: 'hidden',
            products,
          }],
          subtotal: products.reduce((acc, { totalPriceWithBonuses }) => (acc + parseInt(totalPriceWithBonuses)), 0),
        });
      } catch (error) {
        commit('setCart');
      } finally {
        commit('setProgress', false);
      }
    },

    async mergeCart({ commit, dispatch }, {
      anonymousCartId, authCartId, isDelete, territoryId,
    }) {
      try {
        commit('setLoadingStatus', true);

        await this.$apiBFF.mergeCart({
          anonymousCartId, authCartId, isDelete, territoryId,
        });
        await dispatch('loadCart');

        await dispatch('enableUseBonuses');
      } catch (e) {
        dispatch('notifications/create', {
          title: 'Ошибка',
          message: 'При выполнении запроса произошла ошибка',
        }, { root: true });

        return Promise.reject();
      } finally {
        commit('setLoadingStatus', false);
      }
    },

    async loadCartAfterLogin({ commit, getters, dispatch }, { cartIds, territoryId }) {
      if (cartIds.length !== 1) {
        return;
      }

      try {
        if (cartIds.at(0).isAnonymous) {
          // обязательно вызывать mergeCart чтобы закрепить за юзером корзину + Неявно врубает бонусы
          await this.$apiBFF.mergeCart({ anonymousCartId: cartIds.at(0).id, territoryId });
        }

        await dispatch('loadCart');

        // сбрасываем деливери шип группы после логина, если не указан адрес
        if (getters.deliveryInfo?.address?.streetName) return;

        commit('setProgress');
        const products = getters.shippingGroups.reduce((res, { type, products: productsList }) => {
          let response = res;
          if (type !== 'delivery') return response;
          response = response.concat(productsList);
          return response;
        }, []);

        if (products.length) await dispatch('resetShippingGroup', products);
      } finally {
        commit('setProgress', false);
      }
    },

    // TODO: может стоит переименовать? мутации и экшены не назвыают одинаково
    setCart({ commit, dispatch }, cart) {
      commit('setCart', cart);
      if (location?.pathname === '/checkout') {
        return dispatch('fillPickupPoints');
        // важно тут делать Return чтобы промис долетал до serverPrefetch в компоненте
      }
    },

    addCsm(ctx, { skuId = '', price = '', discountPrice = '' } = {}) {
      return this.$api.addCsm({
        wareId: skuId,
        price,
        discountPrice,
      });
    },

    async changeProduct({ dispatch }, {
      lineIds, productId, skuId, quantity, storeId, shippingGroupId,
    } = {}) {
      try {
        await dispatch('add', {
          items: [{
            productId,
            skuId,
            quantity,
          }],
          storeId,
          shippingGroupId,
        });
        await dispatch('remove', { lineIds });
      } catch (e) {
        throw new Error(e);
      }
    },

    async changeQty({ dispatch }, {
      quantity, updatedQuantity, lineIds, productId, skuId, shippingGroupId,
    } = {}) {
      const diff = updatedQuantity - quantity;
      if (diff === 0) return;
      if (diff > 0) {
        await dispatch('add', {
          items: [{
            productId,
            skuId,
            quantity: diff,
          }],
          shippingGroupId,
        });
      } else if (diff < 0) {
        await dispatch('remove', { lineIds: lineIds.slice(lineIds.length + diff, lineIds.length) });
      }
    },

    getShippingGroup({ getters }, { shippingGroupId, storeId } = {}) {
      if (storeId) {
        return {
          idStore: storeId,
          shippingId: shippingGroupId,
        };
      }

      // шиппинг группы кроме нераспределенной
      const existsGroups = getters.shippingGroups
        .filter((group) => group.type !== UNALLOCATED_GROUP && group.type !== SOLDOUT_GROUP);

      if (shippingGroupId && shippingGroupId !== UNALLOCATED_GROUP) {
        return { idStore: undefined, shippingId: shippingGroupId };
      }
      if (!shippingGroupId && existsGroups.length === 1) { // если существует уже шиппинг группа
        const { id } = existsGroups[0];

        return { idStore: undefined, shippingId: id };
      }
      if (shippingGroupId === UNALLOCATED_GROUP) {
        return { idStore: undefined, shippingId: null };
      }

      return {
        idStore: undefined, shippingId: undefined,
      };
    },

    async add({ dispatch, commit }, {
      items = [], pvz = {}, storeId, shippingGroupId,
    } = {}) {
      try {
        commit('setProgress');
        const {
          idStore,
          shippingId,
        } = await dispatch('getShippingGroup', { shippingGroupId, storeId });

        const isNotStore = (pvz?.type === PointTypeEnum.PVZ)
          || (pvz?.type === PointTypeEnum.POSTAMAT)
          || (pvz?.type === PointTypeEnum.POST_OFFICE);

        const cart = await this.$api.addToCart({
          items,
          pvz: isNotStore ? {
            pvzId: pvz.id,
            pvzDeliveryDateFrom: pvz.deliveryDateFrom,
            pvzDeliveryDateTo: pvz.deliveryDateTo,
            pvzDeliveryCost: pvz.deliveryPrice,
          } : null,
          storeId: isNotStore ? null : idStore,
          shippingGroupId: shippingId,
        });

        await dispatch('setCart', cart);
        dispatch('trackCartStats');

        return cart;
      } catch (e) {
        if (e?.response?.status === 404) {
          // SUCCESS
          await dispatch('loadCart');
          return;
        }
        throw new Error(e);
      } finally {
        commit('setProgress', false);
      }
    },

    async remove({ dispatch, commit }, { lineIds = [] } = {}) {
      try {
        commit('setProgress');
        const cart = await this.$api.removeFromCart({ lineIds });
        await dispatch('setCart', cart);
        dispatch('trackCartStats');
      } finally {
        commit('setProgress', false);
      }
    },

    async fillPickupPoints({ state, commit }) {
      // заполняет все точки самовывоза, которые используются в корзине
      const ids = {
        pickup: [],
        thirdparty: [],
      };
      state.shippingGroups.forEach((group) => {
        if (group.type === 'pickup' && !state.pickupStores[group.storeId]) ids.pickup.push(group.storeId);
        if (group.type === 'thirdparty' && !state.thirdpartyStores[group.thirdPartyId]) ids.thirdparty.push(group.thirdPartyId);
      });

      const promises = [];

      // авэйтим чтоб избежать ошибки рендеринга на клиенте
      if (ids.pickup.length) {
        promises.push(Promise.all(ids.pickup.map((id) => this.$api.getStore(id)))
          .then((stores) => {
            stores.forEach((store) => {
              commit('addPickupStore', store);
            });
          }));
      }

      if (ids.thirdparty.length) {
        promises.push(this.$api.getThirdpartyStores(ids.thirdparty)
          .then((thirdparty) => {
            thirdparty.forEach((store) => {
              commit('addThirdpartyStore', store);
            });
          }));
      }

      await Promise.all(promises);
    },

    async setShippingGroup({ dispatch, commit }, { type, value, lineIds }) {
      if (!lineIds?.length) {
        return;
      }
      // value - storeId
      try {
        commit('setLoadingStatus', true);
        commit('setProgress');
        const cart = await this.$api.setShippingGroup({ type, value, lineIds });
        await dispatch('setCart', cart);
        dispatch('saveComment'); // коммент можно указать, только с выбранной шип группой
      } finally {
        commit('setLoadingStatus', false);
        commit('setProgress', false);
      }
    },

    async resetShippingGroup({ dispatch, commit }, products = []) { // переводит в unallocated
      try {
        await commit('setLoadingStatus', true);
        await dispatch('add', {
          items: products.map(({ id, skuId, quantity }) => ({
            productId: id,
            skuId,
            quantity,
          })),
          shippingGroupId: UNALLOCATED_GROUP, // чтоб не добавлял в уже существующую шипинггруппу
        });
        await dispatch('remove', { lineIds: products.map(({ lineIds = [] } = {}) => lineIds).flat() });
      } catch (e) {
        throw new Error(e);
      } finally {
        commit('setLoadingStatus', false);
      }
    },

    /**
     * @deprecated - использовать `apiManager.$api.saveDelivery`
     */
    async saveDelivery({ commit }, form = {}) {
      try {
        commit('setProgress');
        const cart = await this.$api.saveDelivery(form);

        commit('setCart', cart);
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$logger.error(e);
        }
      } finally {
        commit('setProgress', false);
      }
    },

    async saveContact({ dispatch, commit }, form = {}) {
      if (cancelReqContact) {
        cancelReqContact();
      }

      try {
        commit('setProgress');
        const { request, cancel } = this.$api.saveContact(form);
        cancelReqContact = cancel;

        dispatch('setCart', await request);
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.$logger.error(e);
        }
      } finally {
        cancelReqContact = null;
        commit('setProgress', false);
      }
    },

    saveComment({ dispatch, commit, state }, { comment } = {}) {
      commit('setProgress');
      return this.$api.saveComment({ comment: comment ?? state.comment })
        .then((cart) => dispatch('setCart', cart))
        .finally(() => {
          commit('setProgress', false);
        });
    },

    async setPaymentMethod({ dispatch, commit }, method = '') {
      try {
        commit('setLoadingStatus', true);
        const cart = await this.$api.setPaymentMethod(method);
        await dispatch('setCart', cart);
      } catch (e) {
        dispatch(...getErrorNotification(e));
      } finally {
        commit('setProgress', false);
        commit('setLoadingStatus', false);
      }
    },

    setPromo({ dispatch, commit }, code) {
      return this.$api.setPromo(code)
        .then((cart) => dispatch('setCart', cart))
        .finally(() => {
          dispatch('trackCartStats');
          commit('setProgress', false);
        });
    },

    deletePromo({ dispatch, commit }, code) {
      commit('setLoadingStatus', true);
      return this.$api.deletePromo(code)
        .then((cart) => dispatch('setCart', cart))
        .finally(() => {
          dispatch('trackCartStats');
          commit('setLoadingStatus', false);
        });
    },

    async applyPromotions({ dispatch, commit }, apply) {
      try {
        commit('setLoadingStatus', true);
        const cart = await this.$api.applyPromotions(apply);
        await dispatch('setCart', cart);
        dispatch('trackCartStats');
      } catch (e) {
        dispatch(...getErrorNotification(e));
      } finally {
        commit('setLoadingStatus', false);
      }
    },

    async applyBonuses({ dispatch, commit }, apply) {
      try {
        commit('setLoadingStatus', true);
        const cart = await this.$api.applyBonuses(apply);
        await dispatch('setCart', cart);
        dispatch('trackCartStats');
      } catch (e) {
        dispatch(...getErrorNotification(e));
      } finally {
        commit('setLoadingStatus', false);
      }
    },

    async enableUseBonuses({ dispatch, rootGetters, getters }) {
      if (!getters['cart/bonuses']?.applied && rootGetters['user/loggedIn']) {
        if (rootGetters['user/bonuses'] > 0) {
          await dispatch('applyBonuses', true);
        } else {
          await dispatch('applyBonuses', false);
        }
      }
    },

    async trackCartSubmit({ rootState, getters, state }, { orderNumbers, cartId, preselectedPaymentMethod }) {
      const orders = await this.$api.getOrderDetails({ orderNumbers });

      await cartGtmEvents.successSubmitOrder({
        cartId,
        orders,
        subtotal: getters.subtotal,
        discounts: state.cartDetails?.discounts?.discounts,
        preselectedPaymentMethod,
      });

      return commonGtmEvents.viewThanksPageAll({
        orders,
        cartId,
        accountId: rootState.user.account?.id,
      });
    },

    async goPayMostExpensiveOrder({ dispatch }, orderNumbers = [], acquiring) {
      const details = await this.$api.getOrderDetails({ orderNumbers });
      const sortedOrders = details
        .sort((orderA, orderB) => parseFloat(orderB.subtotal, 10) - parseFloat(orderA.subtotal, 10));
      const [mostExpensiveOrder, ...restOrdersNumbers] = sortedOrders;

      return dispatch('goToPayOrder', {
        payOrder: mostExpensiveOrder.orderNumber,
        restOrders: restOrdersNumbers.map((o) => o.orderNumber),
        acquiring,
      });
    },

    async createOrder({ rootGetters }) {
      const communicationCode = getCsmCommunicationCode();
      const params = {
        communicationCode,
      };

      if (communicationCode && !rootGetters['user/loggedIn']) {
        params.register = false;
      }

      const { orderNumbers = [], cartId, preselectedPaymentMethod } = await this.$api.checkout(params);

      return {
        orderNumbers,
        cartId,
        preselectedPaymentMethod,
      };
    },

    async getPaymentFpsQr(ctx, { orderNumbers }) {
      const ordersStorage = createOrdersSessionStorage();
      const promises = orderNumbers.map((orderNumber) => this.$api.getPaymentFpsQr({ orderNumber }));

      const responses = await Promise.allSettled(promises)
        .then((results) => results.map(({ value }) => value));

      ordersStorage.ordersWithFps = responses
        .filter((i) => !!i)
        .reduce((res, item) => {
          res[item.orderNumber] = item;
          return res;
        }, {});

      return ordersStorage.ordersWithFps;
    },

    goToPayOrder(ctx, { payOrder, restOrders = [], acquiring }) {
      typGtmEvents.payOnline({ orderNumber: payOrder });

      return this.$api.getPaymentLink({
        orderNumber: payOrder,
        origin: location.origin,
        restOrdersNumbers: restOrders,
        acquiring,
      })
        .then(({ url }) => {
          typGtmEvents.pageVisible(payOrder);
          location.assign(url);
        })
        .catch(() => {
          const url = `/thankyoupage?orders=${payOrder}`;
          location.assign(url);
        });
    },

    showPayError({ dispatch }, error) {
      if (error?.request?.responseText?.includes('error.cart_version_irrelevant')) {
        dispatch('notifications/create', {
          title: 'Ошибка',
          message: 'Проверьте данные заказа и повторите попытку',
        }, { root: true });

        return;
      }

      dispatch('notifications/create', {
        title: 'Ошибка',
        message: 'Не удалось оплатить заказ. Повторите запрос позже.',
      }, { root: true });
    },

    trackCartStats({
      state, getters, rootGetters, rootState,
    }) {
      if (rootState?.route?.name === 'Checkout') {
        cartGtmEvents.viewPage({
          cartId: state.cartId,
          products: getters.products,
          authEd: rootGetters['user/loggedIn'],
          cartRevenue: getters.subtotal,
          cartQuantity: state.productCount,
        });
      }
    },

    async getDeliveryVariants({ commit, getters }, { lineIds }: { lineIds: string[]; }) {
      const deliveryVariants = await apiManager.$api.getDeliveryVariants({ lineIds });
      const selectedIntervalId = getters.deliverySelectedDate?.intervalId;

      const interval = fromDeliveryVariantsToIntervals(deliveryVariants, selectedIntervalId);

      if (interval && interval.length > 0) {
        commit('setDeliveryInfoInterval', { interval });
      }

      return deliveryVariants;
    },
  },

  mutations: {
    setCart: (state, {
      version = '',
      productCount = 0,
      contact = {},
      comment,
      shippingGroups = [],
      channels = [],
      deliveryInfo = {},
      paymentMethods = [],
      promoCodes = [],
      bonuses = {},
      subtotal = '',
      forbiddenPromotions = [],
      id = '',
      cartDetails = {},
      products = {},
    } = {}) => {
      // Перекрывать дефолтом обязательно, тк может вернуть все поля null
      state.version = version || '';
      state.productCount = productCount || 0;
      state.productsInfo = products || {};

      const phone = contact.phone || '';

      state.contact = { ...contact, phone: phone.replace(/\D/g, '') } || {};
      state.comment = (comment ?? state.comment) || '';

      // Так как от Ostin API не приходит доступность
      const shGroups = shippingGroups.map((group) => ({
        ...group,
        products: group.products.map((product) => {
          // для объединения данных пришедших в разных местах
          const commonProductData = products[product.skuId];

          return {
            ...commonProductData,
            ...product,
          };
        }),
      }));

      state.shippingGroups = shGroups || [];
      state.channels = channels || [];

      const { address, ...restDeliveryInfo } = deliveryInfo || {};

      state.deliveryInfo = { address, ...restDeliveryInfo };
      state.paymentMethods = paymentMethods || [];
      state.forbiddenPromotions = forbiddenPromotions || [];
      state.bonuses = bonuses || {};
      state.promoCodes = promoCodes || [];
      state.subtotal = subtotal || '';
      state.cartId = id;
      state.cartDetails = cartDetails || {};
      state.products = products || {};
    },

    setToCartProgress(state, progress) {
      state.toCartProgress = progress;
    },

    setToCartSuccess(state, success) {
      state.toCartSuccess = success;
    },

    setDeliveryInfoInterval(state, { interval }) {
      state.deliveryInfo = {
        ...state.deliveryInfo,
        interval,
      };
    },

    setCartVersion(state, { version }) {
      state.version = version || '';
    },

    async setCartExist(state, carts = []) {
      state.carts = carts;
    },

    setProgress: (state, progress = true) => {
      state.updateProgress = progress;
    },

    setWidgetDataType: (state, type = '') => {
      state.widgetDataType = type;
    },

    addPickupStore(state, store) {
      state.pickupStores[store.id] = store;
    },

    addThirdpartyStore(state, store) {
      state.thirdpartyStores[store.id] = store;
    },

    subscribe(state, subscribe = true) {
      state.subscribe = subscribe;
    },

    setLoadingStatus(state, value = false) {
      state.isLoading = value;
    },

    setDeliverySelectorInfo(state, info) {
      state.deliverySelectorInfo = { ...info };
    },

    setAdjustScroll(state, val) {
      state.adjustScrollNeeded = val;
    },

    setComment: (state, comment) => {
      state.comment = comment;
    },
  },
};
